import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { AuditInfo } from "../../components/AuditInfo";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarSector,
  crearSector,
  eliminarSector,
  listarSectores,
  type SectorCreatePayload,
  type SectorRead,
  type SectorUpdatePayload,
} from "./api";

export function SectoresListPage(): JSX.Element {
  const [editing, setEditing] = useState<SectorRead | "new" | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["sectores"],
    queryFn: listarSectores,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sectores"
        icon={MapPin}
        subtitle={
          <>
            Los sectores son zonas geográficas o rutas de lectura. Los arranques
            se asignan a un sector. El día de lectura y el día de facturación (del
            1 al 31) se usan como referencia operativa del ciclo mensual.
            {sectoresQ.data && <> ({sectoresQ.data.length} sectores)</>}
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo sector
          </Button>
        }
      />

      {editing && (
        <SectorForm
          sector={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {sectoresQ.isPending && <Cargando />}
      {sectoresQ.isError && (
        <ErrorCarga
          error={sectoresQ.error}
          mensaje="Error al cargar sectores."
          onReintentar={() => void sectoresQ.refetch()}
        />
      )}

      {sectoresQ.data && sectoresQ.data.length === 0 && (
        <EmptyState
          icon={MapPin}
          title="Aún no hay sectores"
          description="Los sectores agrupan a los socios geográficamente y definen los días de lectura y facturación. Necesitas al menos uno antes de empezar a operar."
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Crear primer sector
          </Button>
        </EmptyState>
      )}

      {sectoresQ.data && sectoresQ.data.length > 0 && (
        <TableWrap label="Sectores">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-left">Descripción</th>
                <th scope="col" className="px-3 py-2 text-right">Día lectura</th>
                <th scope="col" className="px-3 py-2 text-right">Día facturación</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {sectoresQ.data.map((s) => (
                <SectorRow
                  key={s.id}
                  sector={s}
                  onEdit={() => setEditing(s)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function SectorRow({
  sector,
  onEdit,
}: {
  sector: SectorRead;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const eliminar = useMutation({
    mutationFn: () => eliminarSector(sector.id),
    onSuccess: () => {
      toast.success(`Sector "${sector.nombre}" eliminado`);
      void qc.invalidateQueries({ queryKey: ["sectores"] });
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo eliminar",
      );
    },
  });

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {sector.codigo ?? "—"}
      </td>
      <td className="px-3 py-2 font-medium text-neutral-900">
        <span className="inline-flex items-center gap-1.5">
          {sector.nombre}
          <AuditInfo
            createdAt={sector.created_at}
            updatedAt={sector.updated_at}
          />
        </span>
        {(sector.latitud != null || sector.longitud != null) && (
          <div className="text-xs text-neutral-500 font-normal">
            {sector.latitud?.toFixed(5)}, {sector.longitud?.toFixed(5)}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-neutral-600 max-w-xs truncate">
        {sector.descripcion ?? "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{sector.dia_lectura}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {sector.dia_facturacion}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          <Button
            variant="linkDanger"
            size="sm"
            icon={Trash2}
            onClick={() => setConfirming(true)}
            disabled={eliminar.isPending}
          >
            Eliminar
          </Button>
        </div>
      </td>

      {confirming && (
        <ConfirmDialog
          open
          title={`Eliminar sector "${sector.nombre}"`}
          description="Se elimina de forma permanente. Falla si el sector todavía tiene arranques asociados — en ese caso reasígnalos primero."
          confirmLabel="Eliminar sector"
          loading={eliminar.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => eliminar.mutate()}
        />
      )}
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta/edición
// ────────────────────────────────────────────────────────────────────────

function SectorForm({
  sector,
  onClose,
}: {
  sector: SectorRead | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = sector !== null;

  const [nombre, setNombre] = useState(sector?.nombre ?? "");
  const [codigo, setCodigo] = useState(sector?.codigo ?? "");
  const [descripcion, setDescripcion] = useState(sector?.descripcion ?? "");
  const [diaLectura, setDiaLectura] = useState(
    String(sector?.dia_lectura ?? 1),
  );
  const [diaFacturacion, setDiaFacturacion] = useState(
    String(sector?.dia_facturacion ?? 5),
  );
  const [latitud, setLatitud] = useState(
    sector?.latitud != null ? String(sector.latitud) : "",
  );
  const [longitud, setLongitud] = useState(
    sector?.longitud != null ? String(sector.longitud) : "",
  );
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: SectorCreatePayload) => crearSector(p),
    onSuccess: (s) => {
      toast.success(`Sector "${s.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["sectores"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: SectorUpdatePayload) =>
      actualizarSector((sector as SectorRead).id, p),
    onSuccess: (s) => {
      toast.success(`Sector "${s.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["sectores"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");

    const dl = Number(diaLectura);
    const df = Number(diaFacturacion);
    if (!Number.isFinite(dl) || dl < 1 || dl > 31)
      return setError("Día de lectura debe estar entre 1 y 31");
    if (!Number.isFinite(df) || df < 1 || df > 31)
      return setError("Día de facturación debe estar entre 1 y 31");

    const lat = latitud.trim() === "" ? null : Number(latitud);
    const lon = longitud.trim() === "" ? null : Number(longitud);
    if (lat !== null && !Number.isFinite(lat))
      return setError("Latitud inválida");
    if (lon !== null && !Number.isFinite(lon))
      return setError("Longitud inválida");

    const payload = {
      nombre: nombre.trim(),
      codigo: codigo.trim() || null,
      descripcion: descripcion.trim() || null,
      dia_lectura: dl,
      dia_facturacion: df,
      latitud: lat,
      longitud: lon,
    };
    if (editando) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    nombre, codigo, descripcion, diaLectura, diaFacturacion, latitud, longitud,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar sector" : "Nuevo sector"}
      subtitle={editando ? sector.nombre : undefined}
      icon={MapPin}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear sector"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Nombre"
          required
          type="text"
          maxLength={100}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={isPending}
          autoFocus
        />
        <Input
          label="Código"
          type="text"
          maxLength={20}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={isPending}
          placeholder="ej. VLA"
        />
        <Input
          label="Descripción"
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={isPending}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Día lectura"
            type="number"
            min={1}
            max={31}
            value={diaLectura}
            onChange={(e) => setDiaLectura(e.target.value)}
            disabled={isPending}
          />
          <Input
            label="Día facturación"
            type="number"
            min={1}
            max={31}
            value={diaFacturacion}
            onChange={(e) => setDiaFacturacion(e.target.value)}
            disabled={isPending}
          />
        </div>
        <Input
          label="Latitud"
          type="number"
          step="0.00001"
          value={latitud}
          onChange={(e) => setLatitud(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Longitud"
          type="number"
          step="0.00001"
          value={longitud}
          onChange={(e) => setLongitud(e.target.value)}
          disabled={isPending}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}
